import { useState } from "react";
import { Camera, User } from "lucide-react";
import { cn } from "@/lib/class-control";

interface ProfileImageUploadProps {
  value: File | string | null;
  onChange: (file: File) => void;
  className?: string;
}

export function ProfileImageUpload({ value, onChange, className }: ProfileImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      onChange(file);
    }
  };

  // Determine image source
  const getImageSrc = () => {
    if (preview) return preview;
    if (typeof value === "string") return value;
    return null;
  };

  const imageSrc = getImageSrc();

  return (
    <div className={cn("relative group", className)}>
      {/* Circular image container */}
      <div className="relative w-[115px] h-[115px] rounded-full overflow-hidden bg-[#15203c] border-2 border-[#5d5d5d]">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-[60px] h-[60px] text-[#5d5d5d]" />
          </div>
        )}

        {/* Camera overlay button - shows on hover */}
        <label
          htmlFor="profile-image-input"
          className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        >
          <div className="w-[43px] h-[43px] bg-[#7846ff] rounded-full flex items-center justify-center">
            <Camera className="w-[24px] h-[24px] text-white" />
          </div>
        </label>

        {/* Hidden file input */}
        <input
          id="profile-image-input"
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
}

ProfileImageUpload.displayName = "ProfileImageUpload";
